<script setup lang="ts">
import { computed, type PropType, reactive, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useTranslate } from '@/shared/lib'
import {
  buildYandexMapsPointUrl,
  coordinatesToLngLat,
} from '@/shared/lib/yandexMaps/buildYandexMapsPointUrl'
import type { InventoryMapInfoType } from '@/entities/Inventory/Main'
import { useNotification, VIcons, VInventAccentStatus } from '@/shared/ui'
import { InventObjectForm } from '@/features/Inventory/ObjectForm'
import { useSendInventPassport } from '../model/api.ts'
import { cloneDeep, isEqual, omitBy, pick } from 'lodash'
import { InventObjectFileLoader } from '@/features/Inventory/ObjectFileLoader'
import { InventObjectLinkButton } from '@/features/Inventory/ObjectLink'
import { InventStats } from '@/entities/Inventory/Stats'
import { useAuth } from '@/entities/Auth'
import { useRoute, useRouter } from 'vue-router'
import { WObjectCoordsManuallyChangeForm } from '@/widgets'
import { InventGroupsMap } from '@/entities/Inventory/GroupsMap'
import { InventPassportSaveDateModal } from '@/features/Inventory/PassportSaveDateModal'
import { ObjectFileGeoGsonBtn, useObjectGeoJson } from '@/features/Inventory/ObjectFileGeoGson'
import { useGlobalSearch } from '@/widgets/GlobalSearch'
import { centerMean, points } from '@turf/turf'
import { buildObjectPassportSavePayload } from '@/features/Inventory/ObjectListEdit/model/api'
import {
  createRuntimeFieldValueMap,
  type RuntimeFieldValueMap,
} from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { usePassportRuntimeV2 } from '@/features/Inventory/PassportRuntimeV2'

const props = defineProps({
  item: {
    type: Object as PropType<InventoryMapInfoType>,
    required: true,
  },
})

const route = useRoute()
const router = useRouter()
const queryClient = useQueryClient()

const data = ref(cloneDeep(props.item))
const oldData = cloneDeep(props.item)

watch(
  () => props.item,
  (next) => {
    data.value = cloneDeep(next)
    Object.assign(oldData, cloneDeep(next))
  },
)
const { isPermissions, hasElementPermission } = useAuth()
const { trs } = useTranslate()

const { send, isPending } = useSendInventPassport()
const { addWarning } = useNotification()

const { downLoadFile, isFetching: isDownloading } = useObjectGeoJson()

const saveFields = [
  'id',
  'name',
  'is_projecting',
  'connection_object_list_id',
  'fields',
  'files',
  'comment',
]

/** Workflow согласования включён для типа объекта. */
const workflowEnabled = computed(() =>
  Boolean(props.item.invent_object_type?.properties?.is_must_accept),
)

const fieldMetadata = computed(
  () => (data.value.invent_object_type?.fields ?? []) as ObjectInventoryField[],
)

/** Собираем payload полей как в PassportRuntimeV2: все поля типа + значения формы. */
function buildSaveFieldValues(): RuntimeFieldValueMap {
  const baseline = createRuntimeFieldValueMap(fieldMetadata.value, oldData.fields)
  const current = createRuntimeFieldValueMap(fieldMetadata.value, data.value.fields)
  const merged: RuntimeFieldValueMap = { ...baseline, ...current }
  for (const [fieldId, value] of Object.entries(current)) {
    if (value === '' && baseline[fieldId]) {
      merged[fieldId] = baseline[fieldId]
    }
  }
  return merged
}

/** Есть ли несохранённые изменения */
const isDirty = computed(() => {
  const oldResult = pick(oldData, saveFields)
  const result = pick(data.value, saveFields)
  const changed = omitBy(result, (value, key) => isEqual(value, oldResult[key]))
  return Object.keys(changed).length > 0
})

const saveModalOpen = ref(false)
const pendingSaveData = ref<any>(null)

/**
 * Функция сохранения данных: проверка dirty, открытие модалки
 */
const saveHandler = () => {
  const oldResult = pick(oldData, saveFields)
  const result = pick(data.value, saveFields)
  const saveData = omitBy(result, (value, key) => isEqual(value, oldResult[key]))

  if (Object.keys(saveData).length === 0) {
    addWarning(trs('itf.no_changes_to_save'))
    return
  }

  pendingSaveData.value = { ...saveData, id: result.id }
  saveModalOpen.value = true
}

/**
 * Применить сохранение с выбранной датой (вызов из модалки)
 */
const applySaveWithDate = async (verDate: string | undefined) => {
  if (!pendingSaveData.value) return

  const connectionId = data.value.connection_object_list_id ?? null
  const payload = buildObjectPassportSavePayload(
    {
      id: data.value.id,
      name: data.value.name,
      is_projecting: data.value.is_projecting,
      connection_object_list_id: connectionId,
      connection_object_id: connectionId,
      fields: buildSaveFieldValues(),
      files: data.value.files,
      comment: data.value.comment ?? '',
      is_original: !workflowEnabled.value,
    },
    fieldMetadata.value,
  )

  const isSaved = await send(payload, verDate)
  pendingSaveData.value = null
  if (!isSaved) return

  Object.assign(oldData, cloneDeep(data.value))
  await queryClient.invalidateQueries({ queryKey: ['load_invent_map_info'] })
}

const emits = defineEmits([
  'updateSelected',
  'updateCoords',
  'deleteFromSelected',
  'mapObjectFocused',
])

/**
 * Функция прокидывания информации о выбранных элементах из статистики
 * @param ids
 */
const selectChildrenHandler = (ids: number[]) => {
  if (!props.item?.id) return

  emits('updateSelected', [props.item.id, ...ids])
}

/**
 * Функция возврата к родительскому элементу
 */
const backParentHandler = (id: number) => {
  emits('updateSelected', [id])
}

/**
 * Ссылка на паспорт элемента (runtime v2)
 * @param id invent_object_id
 */
const getLinkToPassportV2 = (id: number) => {
  return router.resolve({
    name: 'object.inventory.elements.passport-v2',
    params: {
      type: route.params.type,
      object_id: route.params.object_id,
      tab: 'inventory',
      tab_inventory: 'elements',
      invent_object_id: String(id),
    },
  })
}

/** Получаем список группирующих элементов **/
const mapGroups = computed(() => {
  return props.item.groups_map.filter((item) => item.id !== props.item?.parent_object?.id)
})

const controlClearBtn = reactive({
  icon: 'mop',
  text: 'itf.clear_selected',
  color: 'error',
})

/**
 * Функция обработки наведения на кнопку управления выделением
 * @param event
 */
const mouseMoveHandler = (event: MouseEvent) => {
  if (event.shiftKey) {
    controlClearBtn.icon = 'lasso_select'
    controlClearBtn.text = 'itf.clear_selected_with_one'
    controlClearBtn.color = 'success'
  } else {
    controlClearBtn.icon = 'mop'
    controlClearBtn.text = 'itf.clear_selected'
    controlClearBtn.color = 'error'
  }
}

/**
 * Функция центрирования карты по координатам объекта
 */
const showInMapHandler = () => {
  if (!props.item?.coordinates?.length) return

  emits('mapObjectFocused', props.item.coordinates)
}

/**
 * Открыть объект на Яндекс.Картах в новой вкладке
 */
const openInNewTabHandler = () => {
  if (!props.item) return

  const coords = coordinatesToLngLat(props.item.coordinates)
  if (!coords) {
    addWarning(trs('itf.passport_runtime_list_map_disabled_no_geom'))
    return
  }

  window.open(buildYandexMapsPointUrl(coords.lng, coords.lat), '_blank', 'noopener,noreferrer')
}

/**
 * Функция обработки ухода с кнопки управления выделением
 */
const mouseOverLeave = () => {
  controlClearBtn.icon = 'mop'
  controlClearBtn.text = 'itf.clear_selected'
  controlClearBtn.color = 'error'
}

/**
 * Функция удаления элемента из списка выделенных
 */
const deleteHandler = (event: MouseEvent) => {
  if (!props.item) return

  if (event.shiftKey) {
    emits('updateSelected', [props.item.id])
  } else {
    emits('deleteFromSelected', props.item.id)
  }
}

/**
 * Открываем поиск с данными по координатам
 */
const searchModel = useGlobalSearch()
const searchWithCoordsHandler = () => {
  if (!props.item) return

  const coordsCenter = centerMean(points(props.item.coordinates))

  searchModel.query.area = {
    type: 'circle',
    center: coordsCenter.geometry.coordinates,
    radius: 1000,
  }

  searchModel.open = true
}

/** Ссылка на историю изменений */
const historyLink = computed(() => {
  return router.resolve({
    name: 'object.inventory.history',
    params: {
      ...route.params,
      tab: 'inventory',
      tab_inventory: 'history',
    },
  })
})

const passportModel = usePassportRuntimeV2()
</script>

<template>
  <VExpansionPanel density="compact" :min-width="300" :max-width="500">
    <template #title>
      <div class="w-invent-map-info-item__header">
        <div class="w-invent-map-info-item__title">
          <div class="w-invent-map-info-item__title-row">
            <VIcons :type="item.object_type.icon" class="w-invent-map-info-item__icon_active" />

            <div class="w-invent-map-info-item__name">{{ item.name }}</div>
          </div>

          <div
            v-if="item.object_type.object_name"
            class="w-invent-map-info-item__type-name"
          >
            {{ item.object_type.object_name }}
          </div>
        </div>

        <div class="w-invent-map-info-item__links">
          <VTooltip location="top" :open-delay="1000" :text="$t('itf.map')">
            <template #activator="{ props }">
              <VBtn
                icon
                density="compact"
                variant="text"
                v-bind="props"
                @click.stop="showInMapHandler"
              >
                <VIcons type="location_on" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t('itf.open_in_new_tab')">
            <template #activator="{ props }">
              <VBtn
                icon
                density="compact"
                variant="text"
                v-bind="props"
                @click.stop="openInNewTabHandler"
              >
                <VIcons type="open_in_new" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t('itf.search_to_coords')">
            <template #activator="{ props }">
              <VBtn
                icon
                density="compact"
                variant="text"
                v-bind="props"
                color="default"
                @click.stop="searchWithCoordsHandler"
              >
                <VIcons type="search" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t(controlClearBtn.text)">
            <template #activator="{ props }">
              <VBtn
                icon
                density="compact"
                :color="controlClearBtn.color"
                variant="plain"
                v-bind="props"
                @mousedown.stop="deleteHandler"
                @mousemove="mouseMoveHandler"
                @mouseleave="mouseOverLeave"
              >
                <VIcons :type="controlClearBtn.icon" />
              </VBtn>
            </template>
          </VTooltip>
        </div>
      </div>
    </template>

    <template #text>
      <div class="w-invent-map-info-item__sub-header">
        <div class="w-invent-map-info-item__id">Ид.: {{ item.id }}</div>

        <div class="w-invent-map-info-item__bts">
          <VInventAccentStatus v-if="item.accept_status" :status="item.accept_status" />

          <VBtn :to="historyLink" density="compact">Ист. изм.</VBtn>

          <ObjectFileGeoGsonBtn :item />
        </div>
      </div>

      <template
        v-if="item.parent_object && item.parent_object?.name && item.parent_object?.object_type"
      >
        <VDivider class="mt-4" />

        <div class="w-invent-map-info-item__parent">
          {{ item.parent_object?.object_type.object_name }}:

          <a href="#" @click.prevent="backParentHandler(item.parent_object.id)">
            {{ item.parent_object?.name }}
          </a>
        </div>

        <VDivider />
      </template>

      <InventObjectForm v-model="data" />

      <InventGroupsMap
        v-if="mapGroups.length"
        :groups="mapGroups"
        @select-parent="backParentHandler"
      />

      <InventStats
        v-if="Object.keys(item.stats.by_type).length"
        :stats="item.stats"
        @select-children="selectChildrenHandler"
      />

      <InventObjectFileLoader v-model="data" v-if="item.invent_object_type.properties.has_file" />

      <WObjectCoordsManuallyChangeForm :object-id="item.id" @update="emits('updateCoords')" />

      <div class="form__btn_between w-invent-map-info-item__actions">
        <VBtn
          color="primary"
          :loading="isPending"
          :class="{
            disabled:
              !isPermissions(['OBJECT_EDIT']) ||
              !hasElementPermission(item.object_type.id, 'update'),
          }"
          @click="saveHandler"
        >
          {{ $t('itf.save') }}
          <span v-if="isDirty" class="w-invent-map-info-item__dirty-dot" />
        </VBtn>

        <div class="form__btn_right">
          <VTooltip location="top" :open-delay="1000" :text="$t('itf.map_open_passport_modal')">
            <template #activator="{ props }">
              <VBtn variant="outlined" @click="passportModel.openHandler(item.id)" v-bind="props">
                <VIcons type="list_alt" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t('itf.map_open_passport')">
            <template #activator="{ props }">
              <VBtn variant="outlined" :to="getLinkToPassportV2(item.id)" v-bind="props">
                <VIcons type="format_list_bulleted" />
              </VBtn>
            </template>
          </VTooltip>

          <InventObjectLinkButton
            v-if="
              item.invent_object_type.properties.is_connection_to_object &&
              !item.connection_object_list_id &&
              isPermissions(['OBJECT_EDIT']) &&
              hasElementPermission(item.object_type.id, 'update')
            "
            :object-id="item.id"
            :object-type="item.object_type.id"
          />
        </div>
      </div>
    </template>
  </VExpansionPanel>

  <InventPassportSaveDateModal v-model:show="saveModalOpen" @apply="applySaveWithDate" />
</template>
